/**
 * AgentChat — composant de conversation reutilisable.
 *
 * Modes :
 *   - 'general' : assistant generique (FAQ, aide produit)
 *   - 'tender'  : conversation scopée à un tender (analyse, suggestions)
 *   - 'line'    : conversation sur une ligne specifique (justification, alternative)
 *
 * Affiche l'historique + zone de saisie. Les messages sont envoyes via
 * `POST /api/agent/conversations/:id/messages`. La conversation est creee
 * automatiquement au 1er message si conversationId n'est pas fourni.
 *
 * Si le message assistant contient un bloc `action`, on affiche un bouton
 * pour proposer d'appliquer la correction (ex: "Appliquer SKU XYZ").
 */

import { useEffect, useRef, useState } from 'react';
import { Send, Upload, Cog, X, Paperclip } from 'lucide-react';
import { api } from '../lib/api-client';
import { AgentActionCard, type PendingAction } from './AgentActionCard';

type Message = {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  cost_usd?: number | null;
  duration_ms?: number | null;
  metadata_json?: string | null;
  created_at: number;
};

type Conversation = {
  id: string;
  context_kind: 'general' | 'tender' | 'line';
  title: string | null;
  nb_messages: number;
  last_message_at: number | null;
  status: 'active' | 'archived';
};

type AgentChatProps = {
  contextKind: 'general' | 'tender' | 'line';
  tenderId?: string;
  lineId?: string;
  /** Conversation id existante. Si absent, une est creee au 1er envoi. */
  initialConversationId?: string;
  /** Hauteur fixe de la zone messages (default 400px) */
  height?: string;
  /** Callback quand l'agent suggere une correction structurée */
  onSuggestedAction?: (action: { type: string; [k: string]: unknown }) => void;
  /** Callback quand une nouvelle conversation est créée */
  onConversationCreated?: (id: string) => void;
  /**
   * Si defini, autorise l'utilisateur a attacher un fichier (paperclip).
   * Quand l'utilisateur clique "send" avec un fichier attache, c'est ce
   * callback qui est appele a la place du flux conversation normal.
   * Le composant parent gere l'upload (vers /api/tenders, vers R2, etc).
   * Le `message` peut etre vide.
   */
  onAttachFile?: (file: File, message: string) => Promise<void>;
  /** Liste de mime types acceptes par le file picker (default: PDF) */
  attachAccept?: string;
  /** Texte d'aide affiche dans la zone vide */
  emptyHint?: string;
  /** Placeholder de la zone de saisie */
  inputPlaceholder?: string;
  /**
   * Pre-remplit la textarea au montage. L'utilisateur peut editer ou envoyer
   * tel quel. Utilise pour reprendre un message en attente (ex: msg passe via
   * query param depuis /upload).
   */
  initialDraft?: string;
};

export function AgentChat({
  contextKind,
  tenderId,
  lineId,
  initialConversationId,
  height = '400px',
  onSuggestedAction,
  onConversationCreated,
  onAttachFile,
  attachAccept = 'application/pdf',
  emptyHint,
  inputPlaceholder,
  initialDraft,
}: AgentChatProps) {
  const [conversationId, setConversationId] = useState<string | null>(
    initialConversationId ?? null,
  );
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState(initialDraft ?? '');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Charge l'historique si conversationId fourni
  useEffect(() => {
    if (!conversationId) return;
    api
      .get<{ conversation: Conversation; messages: Message[] }>(
        `/api/agent/conversations/${conversationId}`,
        { domainSlug: undefined },
      )
      .then((d) => {
        setConversation(d.conversation);
        setMessages(d.messages);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Erreur'));
  }, [conversationId]);

  // Scroll auto en bas
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, sending]);

  async function send() {
    const content = draft.trim();
    // Si un fichier est attache et qu'un handler existe, on delegue au parent.
    // Le message peut etre vide (juste l'upload), ou accompagne d'un texte.
    if (attachedFile && onAttachFile) {
      setSending(true);
      setError(null);
      try {
        await onAttachFile(attachedFile, content);
        // Le parent navigue habituellement ailleurs ; on reset au cas ou
        setAttachedFile(null);
        setDraft('');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur upload');
      } finally {
        setSending(false);
      }
      return;
    }

    if (!content || sending) return;
    setSending(true);
    setError(null);

    // Optimistic UI : ajoute le message user immediatement
    const userMsgTemp: Message = {
      id: `temp_${Date.now()}`,
      role: 'user',
      content,
      created_at: Date.now(),
    };
    setMessages((m) => [...m, userMsgTemp]);
    setDraft('');

    try {
      let convId = conversationId;

      if (!convId) {
        // Cree la conversation + envoie le 1er message en 1 appel
        const r = await api.post<{
          conversationId: string;
          firstMessage: {
            assistantContent: string;
            tokensIn: number;
            tokensOut: number;
            costUsd: number;
            action?: { type: string; [k: string]: unknown };
          };
        }>(
          '/api/agent/conversations',
          {
            contextKind,
            tenderId,
            lineId,
            initialMessage: content,
          },
          { domainSlug: undefined },
        );
        convId = r.conversationId;
        setConversationId(convId);
        onConversationCreated?.(convId);
        // Replace temp + add assistant
        setMessages([
          { ...userMsgTemp, id: `user_${Date.now()}` },
          {
            id: `asst_${Date.now()}`,
            role: 'assistant',
            content: r.firstMessage.assistantContent,
            cost_usd: r.firstMessage.costUsd,
            created_at: Date.now(),
          },
        ]);
        if (r.firstMessage.action) onSuggestedAction?.(r.firstMessage.action);
      } else {
        // Conversation existante
        const r = await api.post<{
          assistantContent: string;
          costUsd: number;
          durationMs: number;
          action?: { type: string; [k: string]: unknown };
        }>(
          `/api/agent/conversations/${convId}/messages`,
          { content },
          { domainSlug: undefined },
        );
        setMessages((m) => [
          ...m,
          {
            id: `asst_${Date.now()}`,
            role: 'assistant',
            content: r.assistantContent,
            cost_usd: r.costUsd,
            duration_ms: r.durationMs,
            created_at: Date.now(),
          },
        ]);
        if (r.action) onSuggestedAction?.(r.action);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur envoi');
      // Rollback optimistic
      setMessages((m) => m.filter((mm) => mm.id !== userMsgTemp.id));
      setDraft(content); // restore draft
    } finally {
      setSending(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  return (
    <section
      className="tc-panel flex flex-col"
      style={{ height }}
      aria-label={`Conversation agent IA${conversation?.title ? ` : ${conversation.title}` : ''}`}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-ink-700 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="tc-label">
            agent · {contextKind}
            {conversation?.title && (
              <span className="ml-2 text-steel-300 normal-case tracking-normal text-xs truncate">
                {conversation.title}
              </span>
            )}
          </div>
        </div>
        {conversation && (
          <span className="text-2xs text-steel-500 font-mono" aria-label={`${conversation.nb_messages} messages`}>
            {conversation.nb_messages} msg
          </span>
        )}
      </div>

      {/* Messages — log live, lecteur d'ecran annonce les nouveaux messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4 space-y-3"
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-label="Historique de la conversation"
      >
        {messages.length === 0 && !sending && (
          <div className="text-center text-steel-500 text-sm py-8">
            <div className="tc-label mb-2">démarrage</div>
            <p>
              {emptyHint ?? (
                <>
                  {contextKind === 'general' && 'Pose une question sur la plateforme, le matching, le dimensionnement…'}
                  {contextKind === 'tender' && 'Discute du tender courant : justifie un choix, propose une correction.'}
                  {contextKind === 'line' && 'Comprends ou conteste le matching de cette ligne.'}
                </>
              )}
            </p>
          </div>
        )}
        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}
        {sending && (
          <div
            className="text-2xs text-steel-500 font-mono pl-1"
            role="status"
            aria-live="polite"
          >
            agent réfléchit…
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div
          role="alert"
          className="px-4 py-2 border-t border-red-900/60 bg-red-900/20 text-red-300 text-2xs font-mono"
        >
          ERROR · {error}
        </div>
      )}

      {/* Input */}
      <div className="border-t border-ink-700 p-3">
        {/* Attached file pill */}
        {attachedFile && (
          <div className="mb-2 inline-flex items-center gap-2 bg-ink-800 border border-amber-500/40 px-2.5 py-1.5">
            <Paperclip aria-hidden className="w-3.5 h-3.5 text-amber-500" strokeWidth={1.75} />
            <span className="text-2xs font-mono text-amber-500 uppercase tracking-widest">
              fichier
            </span>
            <span className="text-xs font-mono text-steel-100 truncate max-w-xs">
              {attachedFile.name}
            </span>
            <span className="text-2xs font-mono text-steel-500">
              {(attachedFile.size / 1024).toFixed(0)} KB
            </span>
            <button
              type="button"
              onClick={() => {
                setAttachedFile(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              className="text-steel-500 hover:text-red-400 ml-1 inline-flex items-center"
              aria-label="Retirer le fichier"
              title="Retirer"
            >
              <X aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
            </button>
          </div>
        )}

        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={
            inputPlaceholder ??
            'message... (Enter pour envoyer, Shift+Enter pour saut de ligne)'
          }
          rows={2}
          maxLength={4000}
          aria-label="Message a envoyer a l'agent IA"
          aria-describedby="agent-input-help"
          className="w-full bg-ink-900 border border-ink-700 px-3 py-2 text-sm font-mono focus:outline-none focus:border-amber-500 resize-none"
        />
        <span id="agent-input-help" className="sr-only">
          Appuie sur Entree pour envoyer, Maj+Entree pour saut de ligne. Limite 4000 caracteres.
        </span>
        <div className="flex items-center justify-between mt-2 gap-2">
          <div className="flex items-center gap-2">
            {onAttachFile && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={attachAccept}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) setAttachedFile(f);
                  }}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                  className="text-steel-300 hover:text-amber-500 px-2 py-1 disabled:opacity-40 inline-flex items-center"
                  title="Joindre un fichier"
                  aria-label="Joindre un fichier"
                >
                  <Paperclip aria-hidden className="w-4 h-4" strokeWidth={1.75} />
                </button>
              </>
            )}
            <span className="text-2xs text-steel-700 font-mono">
              {draft.length}/4000
            </span>
          </div>
          <button
            onClick={send}
            disabled={
              sending ||
              (draft.trim().length === 0 && !attachedFile)
            }
            className="tc-btn-primary text-2xs inline-flex items-center gap-1.5"
          >
            {sending ? (
              'sending...'
            ) : attachedFile ? (
              <>
                <Upload aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
                <span>envoyer</span>
              </>
            ) : (
              <>
                <Send aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
                <span>send</span>
              </>
            )}
          </button>
        </div>
      </div>
    </section>
  );
}

type ToolCallTrace = {
  name: string;
  input: unknown;
  rowCount: number;
  truncated: boolean;
};

function MessageBubble({ message }: { message: Message }) {
  if (message.role === 'system') return null;

  const isUser = message.role === 'user';

  // Detect action block
  let displayContent = message.content;
  let action: { type: string; [k: string]: unknown } | null = null;
  const actionMatch = message.content.match(/```action\s*\n([\s\S]*?)\n```/);
  if (actionMatch) {
    displayContent = message.content.replace(actionMatch[0], '').trim();
    try {
      action = JSON.parse(actionMatch[1]!);
    } catch {
      // ignore
    }
  }

  // Extract tool calls trace (Phase 22A) + pending actions (Phase 23)
  let toolCalls: ToolCallTrace[] = [];
  let pendingActions: PendingAction[] = [];
  if (message.metadata_json) {
    try {
      const meta = JSON.parse(message.metadata_json) as {
        toolCalls?: ToolCallTrace[];
        pendingActions?: PendingAction[];
      };
      if (Array.isArray(meta.toolCalls)) toolCalls = meta.toolCalls;
      if (Array.isArray(meta.pendingActions)) pendingActions = meta.pendingActions;
    } catch {
      // ignore parse error
    }
  }

  return (
    <article
      className={`flex gap-2 ${isUser ? 'flex-row-reverse' : ''}`}
      role="article"
      aria-label={isUser ? 'Votre message' : "Reponse de l'agent"}
    >
      <div
        className={`tc-status shrink-0 mt-1 ${
          isUser ? 'tc-status-warn' : 'tc-status-ok'
        }`}
        style={{ width: 32, justifyContent: 'center' }}
        aria-hidden
      >
        {isUser ? 'YOU' : 'AGT'}
      </div>
      <div className={`flex-1 min-w-0 ${isUser ? 'text-right' : ''}`}>
        {/* Tool calls trace (Phase 22A) — affichée en haut, avant la réponse */}
        {!isUser && toolCalls.length > 0 && (
          <div className="mb-2 space-y-1">
            {toolCalls.map((tc, i) => (
              <div
                key={i}
                className="inline-flex items-center gap-2 bg-ink-800 border border-ink-700 px-2 py-1 text-2xs font-mono"
              >
                <span className="text-amber-500">⚡</span>
                <span className="text-steel-300">
                  consulté <span className="text-amber-500">{tc.name}</span>
                </span>
                <span className="text-steel-500">
                  · {tc.rowCount} résultat{tc.rowCount !== 1 ? 's' : ''}
                  {tc.truncated && ' (tronqué)'}
                </span>
              </div>
            ))}
          </div>
        )}
        <div
          className={`inline-block max-w-full text-sm whitespace-pre-wrap break-words ${
            isUser
              ? 'bg-ink-800 px-3 py-2 border border-ink-700'
              : 'text-steel-100'
          }`}
        >
          {displayContent}
        </div>
        {action && (
          <div className="mt-2 inline-block border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-2xs">
            <div className="tc-label text-amber-400 mb-1 inline-flex items-center gap-1.5">
              <Cog aria-hidden className="w-3.5 h-3.5" strokeWidth={1.75} />
              <span>suggested action</span>
            </div>
            <pre className="font-mono text-xs text-amber-300 whitespace-pre-wrap">
              {JSON.stringify(action, null, 2)}
            </pre>
          </div>
        )}
        {/* Phase 23 : pending actions à confirmer (chacun avec son widget) */}
        {!isUser && pendingActions.length > 0 && (
          <div className="space-y-2 mt-2">
            {pendingActions.map((pa) => (
              <AgentActionCard key={pa.id} action={pa} />
            ))}
          </div>
        )}
        <time
          className="text-2xs text-steel-700 mt-1 font-mono block"
          dateTime={new Date(message.created_at).toISOString()}
        >
          {new Date(message.created_at).toLocaleTimeString('fr-FR')}
        </time>
      </div>
    </article>
  );
}
